import {dbDelete, dbGet, dbPatch, dbPost} from "utils/ajaxUtils";
import {arrayOfSorts, isBlank, isNoU} from "utils/DBUtils";

const GENERIC_REMOTE_IMAGE_FETCH_ERROR = "Couldn't load this image for cropping.";

// GET /image/fetch's error responses are JSON (see ErrorResponse), but the
// request was made with responseType: 'blob', so a failed response's body
// (available on the AjaxError as `.response`) is a Blob too -- read it back
// out as text before we can JSON.parse it. Blob#text() isn't universally
// available, so fall back to FileReader.
const readBlobAsText = (blob) => {
    if (isNoU(blob)) {
        return Promise.resolve(null);
    }
    if (typeof blob.text === 'function') {
        return blob.text();
    }
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
        reader.readAsText(blob);
    });
};

// Builds the Error that fetchRemoteImage's Promise rejects with, from an
// RxJS 5 AjaxError. `status` is kept on the rejected error (0 when the
// request never reached the server) so callers can decide whether a retry
// makes sense.
const buildRemoteImageFetchError = (ajaxError) => {
    const status = ajaxError && ajaxError.status;
    return readBlobAsText(ajaxError && ajaxError.response)
        .then((text) => {
            let message = GENERIC_REMOTE_IMAGE_FETCH_ERROR;
            if (!isBlank(text)) {
                try {
                    const parsed = JSON.parse(text);
                    if (parsed && !isBlank(parsed.message)) {
                        message = parsed.message;
                    }
                } catch (err) {
                    // Body wasn't JSON (e.g. the request never reached the
                    // server) -- fall back to the generic message.
                }
            }
            const error = new Error(message);
            error.status = status;
            return error;
        })
        .catch(() => {
            const error = new Error(GENERIC_REMOTE_IMAGE_FETCH_ERROR);
            error.status = status;
            return error;
        });
};

// Fetches an image URL through admin-api's GET /image/fetch (see
// RemoteImageFetcher), same-origin, so the bytes can be safely handed to a
// <canvas> for cropping. Resolves to a Blob; rejects with an Error carrying
// a safe, specific message (from the endpoint) and a `.status`.
export const fetchRemoteImage = (url) => new Promise((resolve, reject) => {
    dbGet('image/fetch', {url}, {responseType: 'blob'}).subscribe({
        next: (response) => resolve(response.response),
        error: (ajaxError) => buildRemoteImageFetchError(ajaxError).then(reject)
    });
});

export const uploadOriginal = (name, info) => dbPost("images/originals/upload", {
    name: name,
    info: info
});

export const getPageOfOriginalImages = (count, page, sortOptions, filterOptions) => dbGet("resource/image", {
    count: count,
    page: page,
    sorts: arrayOfSorts(sortOptions.sorts),
    filters: filterOptions.namedFilters
});

export const getOriginalsForResource = (resourceId) => dbGet("images/originals/for", {
    resourceId: resourceId
});

export const saveImageField = (id, field, value) => dbPatch(`resource/image/${id}`, {
    field: field,
    value: value
});

export const deleteOriginalImage = (imageId) => dbDelete(`images/originals/${imageId}`, {});



// WEBPACK FOOTER //
// ./public/api/images.js